import React from 'react';
import { motion } from 'framer-motion';
import { FiUserPlus, FiMessageSquare, FiTrendingUp } from 'react-icons/fi';

const HowItWorks = () => {
  const steps = [
    {
      id: 1,
      icon: FiUserPlus,
      title: "Sign Up & Customize",
      description: "Create your account and set up personalized AI assistants tailored to your specific needs and preferences.",
      gradient: "from-secondary-500 to-purple-600",
      delay: 0
    },
    {
      id: 2,
      icon: FiMessageSquare,
      title: "Start Multi-AI Conversations",
      description: "Ask questions and get responses from multiple AI models simultaneously. Compare different perspectives and approaches.",
      gradient: "from-accent-500 to-cyan-600",
      delay: 0.2
    },
    {
      id: 3,
      icon: FiTrendingUp,
      title: "Analyze & Improve",
      description: "Use smart summaries and insights to make better decisions. Track your productivity improvements over time.",
      gradient: "from-primary-500 to-blue-600",
      delay: 0.4
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="how-it-works" className="py-20 px-6 relative overflow-hidden">
      <div className="container mx-auto">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1],
              rotate: [0, 360, 0]
            }}
            transition={{ 
              duration: 30, 
              repeat: Infinity, 
              ease: "linear" 
            }}
            className="absolute top-1/3 left-1/4 w-72 h-72 bg-gradient-to-r from-secondary-500/15 to-accent-500/15 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ 
              scale: [1.3, 1, 1.3],
              rotate: [360, 0, 360]
            }}
            transition={{ 
              duration: 35, 
              repeat: Infinity, 
              ease: "linear" 
            }}
            className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-gradient-to-r from-accent-500/15 to-primary-500/15 rounded-full blur-3xl"
          />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 relative z-10"
        >
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl font-display font-bold mb-6"
          >
            <span className="gradient-text">How It Works</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Get started with Bervin in three simple steps and unlock the power of multi-AI conversations
          </motion.p>
        </motion.div>

        {/* Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative z-10"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                variants={itemVariants}
                className="relative group"
              >
                {/* Connection Line (Desktop) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-24 left-full w-full h-0.5 bg-gradient-to-r from-white/20 to-transparent z-0">
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: step.delay + 0.5 }}
                      className={`h-full bg-gradient-to-r ${step.gradient} origin-left`}
                    />
                  </div>
                )}

                {/* Step Card */}
                <motion.div
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  className="relative p-8 glass border border-white/10 rounded-3xl hover-lift group-hover:border-white/20 transition-all duration-300"
                >
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-3xl`}></div>
                  
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`w-12 h-12 bg-gradient-to-r ${step.gradient} rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg`}
                    >
                      {step.id}
                      <div className={`absolute inset-0 bg-gradient-to-r ${step.gradient} rounded-xl blur-lg opacity-50`}></div>
                    </motion.div>
                  </div>

                  {/* Icon */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ 
                      duration: 0.8, 
                      delay: step.delay + 0.2,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={`relative w-20 h-20 bg-gradient-to-r ${step.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-xl`}
                  >
                    <step.icon className="w-10 h-10 text-white" />
                    
                    {/* Icon Glow */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${step.gradient} rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300`}></div>
                    
                    {/* Floating particles around icon */}
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{
                          y: [0, -10, 0],
                          opacity: [0, 1, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                        className="absolute w-1 h-1 bg-white rounded-full"
                        style={{
                          left: `${20 + Math.cos(i * 60 * Math.PI / 180) * 30}px`,
                          top: `${20 + Math.sin(i * 60 * Math.PI / 180) * 30}px`,
                        }}
                      />
                    ))}
                  </motion.div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <motion.h3
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: step.delay + 0.3 }}
                      className="text-2xl font-bold text-white mb-4 group-hover:text-white transition-colors duration-300"
                    >
                      {step.title}
                    </motion.h3>
                    
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: step.delay + 0.4 }}
                      className="text-gray-300 leading-relaxed"
                    >
                      {step.description}
                    </motion.p>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-8 h-8 opacity-20">
                    <div className={`w-full h-full bg-gradient-to-br ${step.gradient} rounded-full blur-sm`}></div>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 w-6 h-6 opacity-20">
                    <div className={`w-full h-full bg-gradient-to-br ${step.gradient} rounded-full blur-sm`}></div>
                  </div>

                  {/* Hover Glow Effect */}
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${step.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}></div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16 relative z-10"
        >
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(168, 85, 247, 0.3)"
            }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              try {
                window.open('https://bervin.appiworks.com/app', '_blank', 'noopener,noreferrer');
              } catch (error) {
                window.location.href = 'https://bervin.appiworks.com/app';
              }
            }}
            className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover-lift relative overflow-hidden group cursor-pointer"
          >
            <span className="relative z-10">Get Started Now</span>
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-600 to-accent-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </motion.button>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 1 }}
            className="text-gray-400 mt-4"
          >
            No credit card required • Free to start • Upgrade anytime
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
